import React, { useEffect, useRef } from 'react';
import { Swiper as SwiperType } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import './AtacamaExperiencesSection.css';
import { initializeDragScroll } from '../../utils/interactiveFeatures';
import { images } from '../shared/assets';
import { HandDrawnButton } from '../shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../shared/CTALink/CTALink';

// Default experience images (using Atacama as fallback)
const defaultExperienceImages = {
  atacama: {
    1: "/assets/images/experiences/experience-atacama-01.jpg",
    2: "/assets/images/experiences/experience-atacama-02.jpg",
    3: "/assets/images/experiences/experience-atacama-03.jpg",
    4: "/assets/images/experiences/experience-atacama-04.jpg",
    5: "/assets/images/experiences/experience-atacama-05.jpg"
  },
  patagonia: {
    1: images.experiences.patagonia.experience1,
    2: images.experiences.patagonia.experience2,
    3: images.experiences.patagonia.experience3,
    4: images.experiences.patagonia.experience4
  },
  iguazu: {
    1: "/assets/images/iguazu/experience-1.jpg",
    2: "/assets/images/iguazu/experience-2.png",
    3: "/assets/images/iguazu/experience-3.jpg",
    4: "/assets/images/iguazu/experience-4.jpg",
    5: "/assets/images/iguazu/experience-5.png"
  },
  mendoza: {
    1: "/assets/images/mendoza/experience-1.jpg",
    2: "/assets/images/mendoza/experience-2.jpg",
    3: "/assets/images/mendoza/experience-3.jpg",
    4: "/assets/images/mendoza/experience-4.jpg",
    5: "/assets/images/mendoza/experience-5.jpg"
  },
  'santa-catarina': {
    1: images.experiences.santaCatarina.experience1,
    2: images.experiences.santaCatarina.experience2,
    3: "/assets/images/santa-catarina/experience-3.png",
    4: "/assets/images/santa-catarina/experience-4.png",
    5: images.experiences.santaCatarina.experience5,
  }
};

interface ExperienceCard {
  id?: number;
  image: string;
  tag?: string;
  name?: string;
  title?: string;
  location?: string;
  category?: string;
  description?: string;
}

interface AtacamaExperiencesSectionProps {
  property?: string;
  title?: string;
  description?: string;
  experiences?: ExperienceCard[];
  restaurantTitle?: string;
  restaurantDescription?: string;
}

// Tag Component - Match GlacierVideoExploration
const Tag: React.FC<{ text: string }> = ({ text }) => {
  return (
    <div className="atacama-experience-tag">
      <div className="atacama-experience-tag-text">
        <p>{text}</p>
      </div>
    </div>
  );
};

// Experience Card Component - Match GlacierVideoExploration
const ExperienceCard: React.FC<{ experience: ExperienceCard }> = ({ experience }) => {
  const displayName = experience.name || experience.title || '';
  const displayLocation = experience.location || experience.category || '';
  const displayTag = experience.tag || experience.category || 'EXPERIENCE';
  const displayDescription = experience.description || '';

  const showLinks = false;

  return (
    <div className="atacama-experience-card">
      <div className="atacama-experience-image-container">
        <img
          src={experience.image}
          alt={`${displayName} ${displayLocation}`}
          className="atacama-experience-image"
        />
        {
          false &&
          <Tag text={displayTag} />
        }
      </div>
      <div className="atacama-experience-content">
        <div className="atacama-experience-card-title">
          <p>{displayName}</p>
        </div>
        {displayLocation && (
          <p className="atacama-experience-card-category">
            {displayLocation}
          </p>
        )}
        {displayDescription && (
          <p className="atacama-experience-card-description">
            {displayDescription}
          </p>
        )}
        {
          showLinks &&
          <div className="atacama-experience-card-actions">
            <CTALink
              text="CONTACT US"
              type="primary"
              color="awasi"
              href="/contact"
              className="atacama-experience-link"
            />
          </div>
        }
      </div>
    </div>
  );
};

export const AtacamaExperiencesSection: React.FC<AtacamaExperiencesSectionProps> = ({
  property = "atacama",
  title = "Experience Your Own Path Private Excursions",
  description = "Since opening in 2007, we have continued to offer memorable tailor-made experiences, sharing with our guests the culture, history and landscape of the Atacama Desert. Visit volcanoes, geysers and salt flats, and be amazed seeing how the landscape magically changes according to the light.",
  experiences: customExperiences
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    // Initialize drag scroll for the experiences carousel
    const cleanup = initializeDragScroll('atacama-experiences-carousel');
    return cleanup;
  }, []);

  const handleSeeDestinations = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleArrowClick = (direction: 'left' | 'right') => {
    if (swiperRef.current) {
      if (direction === 'left') {
        swiperRef.current?.slidePrev();
      } else {
        swiperRef.current?.slideNext();
      }
    }
  };


  // Property-specific default experiences
  const getDefaultExperiences = (): ExperienceCard[] => {
    switch (property) {
      case 'atacama':
        return [
          {
            id: 1,
            image: defaultExperienceImages.atacama[1],
            tag: "NATURE",
            name: "Chaxa",
            location: "ATACAMA",
            category: "ATACAMA",
            description: "Chaxa Lagoon is notable for its expansive views of the Atacama basin. The main attractions include the salt flat formations and a diverse variety of birdlife; particularly flamingos, which can be observed depending on the season."
          },
          {
            id: 2,
            image: defaultExperienceImages.atacama[2],
            tag: "ADVENTURE",
            name: "Rainbow Valley",
            location: "ATACAMA",
            category: "ATACAMA",
            description: "Rainbow Valley is a paradise for geology lovers, it stands out for its characteristic landscapes of the Domeyko mountain range, unique colours and a great variety of activity options, including hiking and viewing petroglyphs of archeological and historical significance."
          },
          {
            id: 3,
            image: defaultExperienceImages.atacama[3],
            tag: "HORSEBACK",
            name: "Horseback Riding",
            location: "ATACAMA",
            category: "ATACAMA",
            description: "Explore the world's driest desert on horseback. This excursion takes you through some of Atacama's iconic landscapes: the Valle de Marte, the formations of la Cordillera de la Sal, or the historic Ayllus. The route will be selected based on your riding experience and personal preferences."
          },
          {
            id: 4,
            image: defaultExperienceImages.atacama[4],
            tag: "ASTRONOMY",
            name: "Astronomical Observation",
            location: "ATACAMA",
            category: "ATACAMA",
            description: "The stargazing tour offers a detailed look at the night sky, under some of the most favorable conditions on earth, both with our bare eyes and through a powerful telescope. You will be amazed by the beauty of space while having the opportunity to learn about subjects such as constellations, the celestial sphere, archeoastronomy, cosmology, stellar evolution and space exploration."
          },
          {
            id: 5,
            image: defaultExperienceImages.atacama[5],
            tag: "FLEXIBLE",
            name: "Choose your pace",
            location: "ATACAMA",
            category: "ATACAMA",
            description: "You may choose between full-day excursions with lunch included ( from 4 to 6 hrs.); or two half-day excursions (each one between 2 and 5 hrs.) returning to have lunch at Awasi after the first excursion and heading out again in the afternoon."
          }
        ];

      case 'iguazu':
        return [
          {
            id: 1,
            image: defaultExperienceImages.iguazu[1],
            tag: "NATURE",
            name: "Devil's Throat",
            location: "IGUAZU",
            category: "IGUAZU",
            description: "Experience the most impressive section of Iguazu Falls. The Devil's Throat offers a breathtaking view of the massive U-shaped waterfall with water plunging 82 meters down. Feel the mist on your face and hear the thunderous roar of this natural wonder."
          },
          {
            id: 2,
            image: defaultExperienceImages.iguazu[2],
            tag: "ADVENTURE",
            name: "Great Adventure",
            location: "IGUAZU",
            category: "IGUAZU",
            description: "Navigate the rapids of the Lower Iguazu River and approach the falls by boat. This thrilling experience combines a jungle truck ride through the Atlantic Forest with an exciting boat adventure that takes you close to the waterfalls' base."
          },
          {
            id: 3,
            image: defaultExperienceImages.iguazu[3],
            tag: "WILDLIFE",
            name: "Jungle Safari",
            location: "IGUAZU",
            category: "IGUAZU",
            description: "Explore the Atlantic Forest with expert naturalist guides. Discover the incredible biodiversity including toucans, coatis, butterflies, and if you're lucky, spot a jaguar. Learn about the complex ecosystem that surrounds the falls."
          },
          {
            id: 4,
            image: defaultExperienceImages.iguazu[4],
            tag: "EXCLUSIVE",
            name: "Full Moon Walk",
            location: "IGUAZU",
            category: "IGUAZU",
            description: "Experience the magic of Iguazu Falls under the moonlight. This exclusive nighttime tour offers a unique perspective of the falls, with moonbows appearing in the mist and the sounds of the nocturnal jungle creating an unforgettable atmosphere."
          }
        ];

      case 'mendoza':
        return [
          {
            id: 1,
            image: defaultExperienceImages.mendoza[1],
            tag: "",
            name: "Premium Wine Tasting",
            location: "MENDOZA",
            category: "MENDOZA",
            description: "Visit exclusive boutique wineries in Uco Valley. Meet the winemakers, explore underground cellars, and taste award-winning Malbecs paired with local cuisine. Each experience is tailored to your wine preferences and expertise level."
          },
          {
            id: 2,
            image: defaultExperienceImages.mendoza[2],
            tag: "",
            name: "Aconcagua Base Camp",
            location: "MENDOZA",
            category: "MENDOZA",
            description: "Journey to the base of the highest peak in the Americas. This full-day excursion takes you through spectacular Andean landscapes, with opportunities to spot condors and guanacos along the way to Aconcagua Provincial Park."
          },
          {
            id: 3,
            image: defaultExperienceImages.mendoza[3],
            tag: "",
            name: "Gaucho Experience",
            location: "MENDOZA",
            category: "MENDOZA",
            description: "Ride with local gauchos through vineyards and Andean foothills. This authentic experience includes traditional asado lunch, folk music, and insights into Argentina's cowboy culture amidst stunning mountain scenery."
          },
          {
            id: 4,
            image: defaultExperienceImages.mendoza[4],
            tag: "",
            name: "Harvest Experience",
            location: "MENDOZA",
            category: "MENDOZA",
            description: "Participate in the grape harvest alongside local workers. Hand-pick grapes, learn traditional winemaking techniques, and enjoy a harvest feast with the winery team. Available during harvest season (February-April)."
          }
        ];

      case 'santa-catarina':
        return [
          {
            id: 1,
            image: defaultExperienceImages['santa-catarina'][1],
            tag: "BEACH",
            name: "Secret Beaches",
            location: "SANTA CATARINA",
            category: "SANTA CATARINA",
            description: "Discover hidden beaches accessible only by trail or boat. Your private guide will take you to pristine coves with crystal-clear waters, perfect for swimming, snorkeling, and enjoying the untouched beauty of Brazil's southern coast."
          },
          {
            id: 2,
            image: defaultExperienceImages['santa-catarina'][2],
            tag: "ADVENTURE",
            name: "Atlantic Forest Trek",
            location: "SANTA CATARINA",
            category: "SANTA CATARINA",
            description: "Hike through preserved Atlantic Forest trails to spectacular viewpoints. Spot exotic birds, monkeys, and unique flora while learning about this endangered ecosystem from expert naturalist guides."
          },
          {
            id: 3,
            image: defaultExperienceImages['santa-catarina'][3],
            tag: "MARINE",
            name: "Whale Watching",
            location: "SANTA CATARINA",
            category: "SANTA CATARINA",
            description: "Witness southern right whales during their annual migration (June-November). These gentle giants come remarkably close to shore, offering incredible viewing opportunities from both land-based viewpoints and boat excursions."
          },
          {
            id: 4,
            image: defaultExperienceImages['santa-catarina'][4],
            tag: "CULTURAL",
            name: "Azorean Heritage",
            location: "SANTA CATARINA",
            category: "SANTA CATARINA",
            description: "Explore the unique Azorean culture of Santa Catarina's coastal villages. Visit traditional fishing communities, sample authentic cuisine, and learn about centuries-old traditions preserved by descendants of Portuguese islanders."
          },
          {
            id: 5,
            image: defaultExperienceImages['santa-catarina'][5],
            tag: "CULTURAL",
            name: "Yoga Sessions",
            location: "SANTA CATARINA",
            category: "SANTA CATARINA",
            description: "Participate in Yoga sessions that combine posture, breathwork, and meditation to enhance your connection with nature and promote inner peace. Ideal for an energizing morning start."
          }
        ];

      default:
        return [];
    }
  };

  const defaultExperiences = getDefaultExperiences();

  const experiences = customExperiences || defaultExperiences;

  // Use the arrow image from assets
  const arrowImageUrl = images.carouselArrow;

  const showLinks = false;

  return (
    <section className="atacama-experiences-section">
      {/* Meaningful Exploration Section Only */}
      <div className="atacama-experiences-content">
        <div className="atacama-experiences-text-content">
          <h2 className="atacama-experiences-title">
            {title.split(' ').map((word, index) => (
              <React.Fragment key={index}>
                {word}{' '}
                {index === 3 && <br />}
              </React.Fragment>
            ))}
          </h2>
          <div className="atacama-experiences-description-wrapper">
            <p className="atacama-experiences-description">
              {description}
            </p>
            {
              showLinks &&
              <HandDrawnButton
                text="LEARN MORE"
                onClick={handleSeeDestinations}
                variant={property as any}
                color="awasi"
                animation="stroke-to-fill"
                size="normal"
                className="atacama-experiences-learn-button"
              />
            }
          </div>
        </div>

        {/* Carousel Section */}
        <div className="glacier-video-exploration__carousel-container" ref={carouselRef}>
          <Swiper
            freeMode
            navigation
            spaceBetween={20}
            slidesPerView={1}
            className='glacier-video-exploration__carousel-swiper'
            breakpoints={{
              1024: {
                slidesPerView: 2,
                spaceBetween: 30,
              },
            }}
            onSwiper={(currentSwiper: SwiperType) => swiperRef.current = currentSwiper}
          >
            {experiences.map((experience) => (
              <SwiperSlide key={experience.id}>
                <ExperienceCard key={experience.id} experience={experience} />
              </SwiperSlide>
            ))}

          </Swiper>
          <div className="glacier-video-exploration__arrow left" onClick={() => handleArrowClick('left')}>
            <img
              src={images.carouselArrow}
              alt="Scroll to see more destinations"
              className="glacier-video-exploration__arrow-image"
            />
          </div>

          <div className="glacier-video-exploration__arrow" onClick={() => handleArrowClick('right')}>
            <img
              src={images.carouselArrow}
              alt="Scroll to see more destinations"
              className="glacier-video-exploration__arrow-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AtacamaExperiencesSection;